import { forwardRef, Fragment } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import {
    baselineOffset,
    useFitFontSize,
    useFitFontSizeToLines,
    VALUE_INK_SHADOW,
} from '../shared/formFieldEmphasis';
import { buildNidCorrectionLetter, cellKeys, CORRECTION_ROWS } from './nidCorrectionFormEngine';
import {
    BASELINE_RATIO,
    CELL_VALUE_TYPE,
    LEADER,
    LINE_BOX,
    NAME_NID_COLON_X,
    PAGE1_FIELDS,
    PAGE1_RECEIPT,
    PAGE1_TAIL_FIELDS,
    PAGE1_TEXT,
    PAGE2_RECEIPT,
    PAGE2_TEXT,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    SIGNATURE,
    TABLE,
    TABLE_ROW_HEIGHT,
    TYPE,
    VALUE_INSET,
    VALUE_LIFT,
    VALUE_TYPE,
} from './nidCorrectionMetrics';

export const NID_CORRECTION_PAGE_WIDTH = PAGE_WIDTH;
export const NID_CORRECTION_PAGE_HEIGHT = PAGE_HEIGHT;

const LEADER_TEXT = LEADER.char.repeat(LEADER.count);

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: TYPE.body.size,
    overflow: 'hidden',
};

/** A measured baseline becomes the CSS top of the line box. */
const topFor = (baseline, size) => baseline - size * BASELINE_RATIO;

/**
 * Rows are placed by baseline. A line box 1.5 em tall leaves room for dots and
 * descenders inside the hidden overflow — padding would do the same in the
 * browser, but html2canvas shifts the text by it, so the export drifted down.
 */
const textStyle = (size, weight) => ({
    position: 'absolute',
    fontSize: size,
    fontWeight: weight,
    lineHeight: LINE_BOX,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
});

/** Printed text of the form, anchored by baseline and by left / right / centre. */
function Line({
    y,
    x,
    right,
    center,
    size = TYPE.body.size,
    weight = TYPE.body.weight,
    underline,
    children,
}) {
    const style = { ...textStyle(size, weight), top: topFor(y, size) };
    if (center != null) {
        style.left = center;
        style.transform = 'translateX(-50%)';
    } else if (right != null) {
        style.right = PAGE_WIDTH - right;
    } else {
        style.left = x;
    }
    if (underline) style.textDecoration = 'underline';
    return <span style={style}>{children}</span>;
}

/** One printed leader: real dots, clipped to the span the form gives it. */
function Leader({ y, from, to, size = TYPE.body.size }) {
    return (
        <span
            aria-hidden
            data-leader
            style={{
                ...textStyle(size, TYPE.body.weight),
                top: topFor(y, size),
                left: from,
                width: Math.max(0, to - from),
                overflow: 'hidden',
                letterSpacing: LEADER.letterSpacing,
            }}
        >
            {LEADER_TEXT}
        </span>
    );
}

/**
 * The short dashed rules the form prints above each signature. They are drawn
 * as a border rather than as dashes in the font, so html2canvas cannot re-space
 * them the way it re-measures text.
 */
function DashRule({ y, from, to }) {
    return (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                left: from,
                top: y,
                width: Math.max(0, to - from),
                height: 0,
                borderTop: '1px dashed #111',
            }}
        />
    );
}

/**
 * A typed answer, sitting just above its dotted line and clipped to it. It
 * prints bigger and heavier than the form's own text, and drops back toward the
 * body size only when the blank is too short for the answer.
 */
function Value({ y, from, to, type = VALUE_TYPE, children }) {
    const maxWidth = Math.max(0, to - from - VALUE_INSET - 2);
    const [ref, size] = useFitFontSize(type.size, type.minSize, [children, maxWidth]);

    if (!children) return null;
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            style={{
                ...textStyle(size, type.weight),
                top: topFor(y - VALUE_LIFT, size),
                // A little air after the printed label, like a written form.
                left: from + VALUE_INSET,
                maxWidth,
                textShadow: VALUE_INK_SHADOW,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {children}
        </span>
    );
}

/** Label, colon, leader and value for one blank above the table. */
function NameNidField({ field, t, values }) {
    return (
        <>
            <Line y={field.y} x={field.x}>{t(`nidCorrectionForm.preview.${field.label}`)}</Line>
            <Line y={field.y} x={NAME_NID_COLON_X}>:</Line>
            <Leader y={field.y} from={field.from} to={field.to} />
            <Value y={field.y} from={field.from} to={field.to}>{values[field.value]}</Value>
        </>
    );
}

/**
 * The item ৩ grid. Rules are drawn as absolutely placed 1 px boxes rather than
 * as one bordered table, because html2canvas collapses table borders
 * unpredictably and the printed page has to line up with the government form.
 */
function CorrectionTable({ d, t }) {
    const { cols, top, headBottom, bottom, labelX, labelBaselineOffset, cellPadX } = TABLE;
    const left = cols[0];
    const right = cols[cols.length - 1];
    const rules = [top, headBottom];
    for (let i = 1; i <= TABLE.rowCount; i += 1) {
        rules.push(headBottom + i * TABLE_ROW_HEIGHT);
    }

    const headCenter = (i) => (cols[i] + cols[i + 1]) / 2;

    return (
        <>
            {rules.map((y) => (
                <span
                    key={`h-${y}`}
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left,
                        top: y,
                        width: right - left,
                        height: 0,
                        borderTop: '1px solid #111',
                    }}
                />
            ))}
            {cols.map((x) => (
                <span
                    key={`v-${x}`}
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: x,
                        top,
                        width: 0,
                        height: bottom - top,
                        borderLeft: '1px solid #111',
                    }}
                />
            ))}

            <Line y={TABLE.headLine1Y} center={headCenter(0)} size={TYPE.tableHead.size} weight={TYPE.tableHead.weight} underline>
                {t('nidCorrectionForm.preview.tableHeadSubject')}
            </Line>
            <Line y={TABLE.headLine1Y} center={headCenter(1)} size={TYPE.tableHead.size} weight={TYPE.tableHead.weight} underline>
                {t('nidCorrectionForm.preview.tableHeadCurrent1')}
            </Line>
            <Line y={TABLE.headLine2Y} center={headCenter(1)} size={TYPE.tableHead.size} weight={TYPE.tableHead.weight} underline>
                {t('nidCorrectionForm.preview.tableHeadCurrent2')}
            </Line>
            <Line y={TABLE.headLine1Y} center={headCenter(2)} size={TYPE.tableHead.size} weight={TYPE.tableHead.weight} underline>
                {t('nidCorrectionForm.preview.tableHeadCorrected')}
            </Line>
            <Line y={TABLE.headLine1Y} center={headCenter(3)} size={TYPE.tableHead.size} weight={TYPE.tableHead.weight} underline>
                {t('nidCorrectionForm.preview.tableHeadDocs')}
            </Line>

            {CORRECTION_ROWS.map((row, index) => {
                const rowTop = headBottom + index * TABLE_ROW_HEIGHT;
                const baseline = rowTop + labelBaselineOffset;
                const keys = cellKeys(row.key);
                // Cells have no dots to clear, so the lift `Value` applies for
                // a dotted blank is added back: an answer prints on its row's
                // own baseline, level with the subject beside it.
                const cell = (col, valueKey) => (
                    <Value
                        y={baseline + VALUE_LIFT}
                        from={cols[col] + cellPadX}
                        to={cols[col + 1] - cellPadX}
                        type={CELL_VALUE_TYPE}
                    >
                        {d[valueKey]}
                    </Value>
                );
                return (
                    <Fragment key={row.key}>
                        <Line y={baseline} x={labelX} size={TYPE.tableLabel.size}>
                            {`(${row.letter})  ${t(`nidCorrectionForm.preview.row_${row.key}`)}`}
                        </Line>
                        {cell(1, keys.current)}
                        {cell(2, keys.corrected)}
                        {cell(3, keys.docs)}
                    </Fragment>
                );
            })}
        </>
    );
}

/**
 * A signature-block answer that is allowed to wrap.
 *
 * It is a div rather than a span on purpose: the export walks every span and
 * pins it to a one-line box, which would leave the second line out of the PDF.
 * It carries `data-wrap-value` so the export can nudge its baseline the way it
 * already does for the instruction list, which is the other block here set as
 * running text.
 */
function WrappingValue({ y, from, to, lines, children }) {
    const width = Math.max(0, to - from - VALUE_INSET - 2);
    const lh = SIGNATURE.lineHeight;
    const [ref, size] = useFitFontSizeToLines(
        VALUE_TYPE.size, VALUE_TYPE.minSize, lines, lh, [children, width],
    );

    if (!children) return null;
    return (
        <div
            ref={ref}
            data-field-value
            data-wrap-value
            data-filled="true"
            style={{
                position: 'absolute',
                left: from + VALUE_INSET,
                // placed so the first line sits on the row's own baseline
                top: y - baselineOffset(size, size * lh),
                width,
                fontSize: size,
                fontWeight: VALUE_TYPE.weight,
                lineHeight: lh,
                fontFamily: 'inherit',
                textShadow: VALUE_INK_SHADOW,
                overflow: 'hidden',
                maxHeight: size * lh * lines,
            }}
        >
            {children}
        </div>
    );
}

/** One column of the applicant / guardian signature block. */
function SignatureColumn({ side, captionKey, d, t }) {
    const col = SIGNATURE[side];
    return (
        <>
            <DashRule y={SIGNATURE.dashY} from={col.dashFrom} to={col.dashTo} />
            <Line y={SIGNATURE.captionY} x={col.x}>
                {t(`nidCorrectionForm.preview.${captionKey}`)}
            </Line>
            {SIGNATURE.rows.map((row) => (
                <Fragment key={`${side}-${row.label}`}>
                    <Line y={row.y} x={col.x}>{t(`nidCorrectionForm.preview.${row.label}`)}</Line>
                    {row.maxLines > 1 ? (
                        <WrappingValue
                            y={row.y}
                            from={col.valueFrom}
                            to={col.valueTo}
                            lines={row.maxLines}
                        >
                            {d[row[side]]}
                        </WrappingValue>
                    ) : (
                        <Value y={row.y + VALUE_LIFT} from={col.valueFrom} to={col.valueTo}>
                            {d[row[side]]}
                        </Value>
                    )}
                </Fragment>
            ))}
        </>
    );
}

function PageOne({ d, t }) {
    const p = PAGE1_TEXT;
    const r = PAGE1_RECEIPT;

    return (
        <div data-nid-correction-page style={PAGE_STYLE}>
            <Line y={p.title.y} center={p.title.center} size={TYPE.title.size} weight={TYPE.title.weight}>
                {t('nidCorrectionForm.preview.formNo')}
            </Line>
            <Line y={p.rule.y} center={p.rule.center} size={TYPE.rule.size} weight={TYPE.rule.weight}>
                {t('nidCorrectionForm.preview.rule')}
            </Line>
            <Line y={p.mainTitle.y} center={p.mainTitle.center} size={TYPE.mainTitle.size} weight={TYPE.mainTitle.weight} underline>
                {t('nidCorrectionForm.preview.title')}
            </Line>

            <Line y={p.serial.y} right={p.serial.labelRight}>
                {t('nidCorrectionForm.preview.serialLabel')}
            </Line>
            <Line y={p.serial.y} x={p.serial.colonX}>:</Line>
            <Leader y={p.serial.y} from={p.serial.from} to={p.serial.to} />
            <Line y={p.serial.y} x={p.serial.noteX}>
                {t('nidCorrectionForm.preview.serialNote')}
            </Line>

            <Line y={p.holderHead.y} x={p.holderHead.x}>
                {t('nidCorrectionForm.preview.holderHead')}
            </Line>
            <Line y={p.guardianHead.y} x={p.guardianHead.x}>
                {t('nidCorrectionForm.preview.guardianHead')}
            </Line>
            {PAGE1_FIELDS.map((field) => (
                <NameNidField key={field.label} field={field} t={t} values={d} />
            ))}

            <Line y={p.tableHead.y} x={p.tableHead.x}>
                {t('nidCorrectionForm.preview.tableIntro')}
            </Line>
            <CorrectionTable d={d} t={t} />

            {PAGE1_TAIL_FIELDS.map((field) => (
                <Fragment key={field.label}>
                    <Line y={field.y} x={field.x}>{t(`nidCorrectionForm.preview.${field.label}`)}</Line>
                    <Value y={field.y + VALUE_LIFT} from={field.from} to={field.to}>{d[field.value]}</Value>
                </Fragment>
            ))}

            <SignatureColumn side="left" captionKey="guardianSignCaption" d={d} t={t} />
            <SignatureColumn side="right" captionKey="applicantSignCaption" d={d} t={t} />

            <Leader y={r.separator.y} from={r.separator.from} to={r.separator.to} />
            <Line y={r.part.y} center={r.part.center} weight={700}>
                {t('nidCorrectionForm.preview.page1PartLabel')}
            </Line>
            <Line y={r.title.y} center={r.title.center} size={TYPE.receiptHead.size} weight={TYPE.receiptHead.weight} underline>
                {t('nidCorrectionForm.preview.page1ReceiptTitle')}
            </Line>
            <Line y={r.rule.y} center={r.rule.center} size={TYPE.rule.size}>
                {t('nidCorrectionForm.preview.page1ReceiptRule')}
            </Line>
            <Line y={r.serial.y} right={r.serial.labelRight}>
                {t('nidCorrectionForm.preview.serialLabel')}
            </Line>
            <Line y={r.serial.y} x={r.serial.colonX}>:</Line>
            <Leader y={r.serial.y} from={r.serial.from} to={r.serial.to} />

            {r.rows.map((row) => (
                <Fragment key={row.label}>
                    <Line y={row.y} x={row.x}>{t(`nidCorrectionForm.preview.${row.label}`)}</Line>
                    {row.noColon ? null : <Line y={row.y} x={r.colonX}>:</Line>}
                </Fragment>
            ))}

            <Line y={r.seal.y} x={r.seal.x}>{t('nidCorrectionForm.preview.seal')}</Line>
            <DashRule y={r.officerDash.y} from={r.officerDash.from} to={r.officerDash.to} />
            <Line y={r.officerCaption.y} center={r.officerCaption.center}>
                {t('nidCorrectionForm.preview.officerSignature')}
            </Line>
        </div>
    );
}

/**
 * The instruction list. It is the one block on either page set as running text:
 * the original wraps and justifies it inside a single column, and re-flowing it
 * keeps Bengali conjuncts from breaking where the original never would.
 */
const INSTRUCTION_TEXT_STYLE = {
    fontSize: TYPE.instruction.size,
    fontWeight: TYPE.instruction.weight,
    lineHeight: PAGE2_TEXT.instructions.lineHeight,
    fontFamily: 'inherit',
};

function Instructions({ t }) {
    const { top, left, width, gutter, lineHeight, paragraphGap } = PAGE2_TEXT.instructions;
    const items = t('nidCorrectionForm.preview.instructions', { returnObjects: true });
    const list = Array.isArray(items) ? items : [];

    return (
        <div
            data-instructions
            style={{
                position: 'absolute',
                left,
                top,
                width,
                fontSize: TYPE.instruction.size,
                lineHeight,
                textAlign: 'justify',
            }}
        >
            {list.map((item) => (
                <div
                    key={item.n}
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: paragraphGap,
                        marginLeft: item.sub ? gutter : 0,
                    }}
                >
                    {/*
                      * The app's own stylesheet sets a font-size on bare
                      * `span`, which beats what these would otherwise inherit
                      * from the block — the type has to be restated inline or
                      * the instructions set at 12 px and run off the page.
                      */}
                    <span style={{ ...INSTRUCTION_TEXT_STYLE, flex: `0 0 ${gutter}px`, whiteSpace: 'nowrap' }}>
                        {item.n}
                    </span>
                    <span style={{ ...INSTRUCTION_TEXT_STYLE, flex: '1 1 auto', textAlign: 'justify' }}>
                        {item.text}
                    </span>
                </div>
            ))}
        </div>
    );
}

function PageTwo({ t }) {
    const p = PAGE2_TEXT;
    const r = PAGE2_RECEIPT;

    return (
        <div data-nid-correction-page style={PAGE_STYLE}>
            <Line y={p.title.y} center={p.title.center} size={TYPE.mainTitle.size} weight={TYPE.mainTitle.weight} underline>
                {t('nidCorrectionForm.preview.instructionsTitle')}
            </Line>
            <Instructions t={t} />

            <Leader y={r.separator.y} from={r.separator.from} to={r.separator.to} />
            <Line y={r.part.y} center={r.part.center} weight={700}>
                {t('nidCorrectionForm.preview.page2PartLabel')}
            </Line>
            <Line y={r.title.y} center={r.title.center} size={TYPE.receiptHead.size} weight={TYPE.receiptHead.weight} underline>
                {t('nidCorrectionForm.preview.page2ReceiptTitle')}
            </Line>
            <Line y={r.rule.y} center={r.rule.center} size={TYPE.rule.size}>
                {t('nidCorrectionForm.preview.page2ReceiptRule')}
            </Line>

            {r.rows.map((row) => (
                <Fragment key={row.label}>
                    <Line y={row.y} x={row.x}>{t(`nidCorrectionForm.preview.${row.label}`)}</Line>
                    {row.leader ? (
                        <>
                            <Line y={row.y} x={r.colonX}>:</Line>
                            <Leader y={row.y} from={r.leader.from} to={r.leader.to} />
                        </>
                    ) : null}
                </Fragment>
            ))}

            <Line y={r.seal.y} x={r.seal.x}>{t('nidCorrectionForm.preview.seal')}</Line>
            <DashRule y={r.officerDash.y} from={r.officerDash.from} to={r.officerDash.to} />
            <Line y={r.officerCaption.y} center={r.officerCaption.center}>
                {t('nidCorrectionForm.preview.officerSignature')}
            </Line>
        </div>
    );
}

function NidCorrectionFormPreview({ form }, ref) {
    const { t } = useTranslation();
    const d = buildNidCorrectionLetter(form);

    return (
        <div ref={ref} data-nid-correction-document style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageOne d={d} t={t} />
            <PageTwo t={t} />
        </div>
    );
}

export default forwardRef(NidCorrectionFormPreview);
